import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CHECKOUT_EVENTS,
  CHECKOUT_MESSAGE_SOURCES,
  CHECKOUT_SESSION_STATES,
  CHECKOUT_TAB_STATES,
  CHECKOUT_TAB_POLL_MS,
  CHECKOUT_ROUTE_PATH,
} from '../constants/bookingConst';
import {
  createCheckoutSessionId,
  buildCheckoutUrl,
  saveCheckoutSession,
  clearCheckoutSession,
  pruneStaleCheckoutSessions,
  openCheckoutChannel,
  openCheckoutTab,
  isTabOpen,
} from '../utils/checkoutSessionUtil';

/**
 * Booking-tab side of the checkout tab: opens it, hands it the booking,
 * listens for what the guest does there, and notices when it is closed.
 *
 * Handlers are read through a ref so the caller can pass fresh closures on
 * every render (they read current booking state) without re-subscribing.
 *
 * Used by the court-booking flow (App.jsx) and the Open Play join flow
 * (OpenPlayModal.jsx); `options.routePath` picks which checkout page opens.
 *
 * @param {Object} handlers
 * @param {(bookingData: Object) => void} handlers.onReceiptUploaded - receipt attached; booking is now submitted.
 * @param {(bookingData: Object) => void} handlers.onSubmitted - guest tapped Submit Booking; checkout tab is closing.
 * @param {() => void} handlers.onCancelled - guest cancelled from the checkout tab; reservation already released.
 * @param {() => void} handlers.onExpired - payment timer ran out in the checkout tab; reservation already released.
 * @param {() => void} handlers.onTabClosed - guest closed the checkout tab without finishing.
 * @param {Object} [options]
 * @param {string} [options.routePath] - checkout page route; defaults to CHECKOUT_ROUTE_PATH.
 * @returns {{
 *   tabState: string,
 *   prepare: () => void,
 *   launch: (bookingData: Object, bookingKeys: string[], context?: Object) => void,
 *   reopen: () => void,
 *   end: () => void,
 *   isCheckoutTabOpen: () => boolean,
 * }}
 */
export const useCheckoutTab = (handlers, { routePath = CHECKOUT_ROUTE_PATH } = {}) => {
  const [tabState, setTabState] = useState(CHECKOUT_TAB_STATES.IDLE);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const sessionIdRef = useRef(null);
  const sessionRef = useRef(null);
  const tabRef = useRef(null);
  const channelRef = useRef(null);

  /**
   * Closes the message channel for the current session, if any.
   */
  const closeChannel = useCallback(() => {
    channelRef.current?.close();
    channelRef.current = null;
  }, []);

  /**
   * Forgets the current session: closes the channel and removes the stored
   * payload. Leaves the tab itself alone — callers decide about that.
   */
  const teardown = useCallback(() => {
    closeChannel();
    clearCheckoutSession(sessionIdRef.current);
    sessionIdRef.current = null;
    sessionRef.current = null;
  }, [closeChannel]);

  /**
   * Closes the checkout tab if this tab opened it and it is still open.
   */
  const closeTab = useCallback(() => {
    if (isTabOpen(tabRef.current)) {
      try { tabRef.current.close(); } catch (error) { /* already gone */ }
    }
    tabRef.current = null;
  }, []);

  /**
   * Ends a checkout that finished in the checkout tab.
   * @param {Function|undefined} handler - the caller's handler to run afterwards.
   * @param {any} [arg]
   */
  const finishFromCheckout = useCallback((handler, arg) => {
    closeTab();
    teardown();
    setTabState(CHECKOUT_TAB_STATES.IDLE);
    try { window.focus(); } catch (error) { /* focus is best-effort */ }
    handler?.(arg);
  }, [closeTab, teardown]);

  const handleMessage = useCallback((type, payload) => {
    switch (type) {
      case CHECKOUT_EVENTS.REQUEST_SESSION:
        // The checkout tab booted after the booking was handed over, or has
        // no localStorage — send it again.
        if (sessionRef.current) channelRef.current?.post(CHECKOUT_EVENTS.SESSION, sessionRef.current);
        break;
      case CHECKOUT_EVENTS.RECEIPT_UPLOADED:
        if (sessionRef.current && payload?.bookingData) {
          sessionRef.current = { ...sessionRef.current, bookingData: payload.bookingData };
        }
        handlersRef.current.onReceiptUploaded?.(payload?.bookingData);
        break;
      case CHECKOUT_EVENTS.SUBMITTED:
        finishFromCheckout(handlersRef.current.onSubmitted, payload?.bookingData);
        break;
      case CHECKOUT_EVENTS.CANCELLED:
        finishFromCheckout(handlersRef.current.onCancelled);
        break;
      case CHECKOUT_EVENTS.EXPIRED:
        finishFromCheckout(handlersRef.current.onExpired);
        break;
      default:
        break;
    }
  }, [finishFromCheckout]);

  /**
   * Opens the checkout tab straight away, before the booking exists.
   * Call synchronously from the submit click: browsers only allow
   * window.open during a user gesture, and writing the booking takes long
   * enough (Firebase transactions per date) that the gesture would be gone
   * by then. The tab shows a spinner until launch() hands the booking over.
   */
  const prepare = useCallback(() => {
    closeTab();
    teardown();
    pruneStaleCheckoutSessions();

    const sessionId = createCheckoutSessionId();
    sessionIdRef.current = sessionId;
    saveCheckoutSession(sessionId, { state: CHECKOUT_SESSION_STATES.PREPARING });
    channelRef.current = openCheckoutChannel(sessionId, CHECKOUT_MESSAGE_SOURCES.BOOKING, handleMessage);

    tabRef.current = openCheckoutTab(buildCheckoutUrl(sessionId, routePath));
    setTabState(tabRef.current ? CHECKOUT_TAB_STATES.PREPARING : CHECKOUT_TAB_STATES.BLOCKED);
  }, [closeTab, teardown, handleMessage, routePath]);

  /**
   * Hands the created record to the checkout tab.
   * @param {Object} bookingData - the booking (or Open Play join) to pay for.
   * @param {string[]} bookingKeys - its Firebase record id(s).
   * @param {Object} [context] - anything else the checkout page needs to render it.
   */
  const launch = useCallback((bookingData, bookingKeys, context = null) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    const session = { state: CHECKOUT_SESSION_STATES.READY, bookingData, bookingKeys, context };
    sessionRef.current = session;
    saveCheckoutSession(sessionId, session);
    channelRef.current?.post(CHECKOUT_EVENTS.SESSION, session);

    if (isTabOpen(tabRef.current)) {
      setTabState(CHECKOUT_TAB_STATES.OPEN);
    } else {
      // Blocked outright, or closed by the guest while the booking was being
      // written. Either way the card offers a button to open it.
      setTabState(tabRef.current ? CHECKOUT_TAB_STATES.CLOSED : CHECKOUT_TAB_STATES.BLOCKED);
    }
  }, []);

  /**
   * Brings the checkout tab back — focuses it if open, otherwise opens it
   * again on the same session. Call from a click.
   */
  const reopen = useCallback(() => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    if (isTabOpen(tabRef.current)) {
      try { tabRef.current.focus(); } catch (error) { /* focus is best-effort */ }
      return;
    }
    // Re-store the payload: a reopened tab reads it on boot.
    if (sessionRef.current) saveCheckoutSession(sessionId, sessionRef.current);
    tabRef.current = openCheckoutTab(buildCheckoutUrl(sessionId, routePath));
    setTabState(tabRef.current ? CHECKOUT_TAB_STATES.OPEN : CHECKOUT_TAB_STATES.BLOCKED);
  }, [routePath]);

  /**
   * Ends the checkout from this tab (booking failed, cancelled here, or
   * finished here): tells the checkout tab to close, closes it, forgets the
   * session.
   */
  const end = useCallback(() => {
    channelRef.current?.post(CHECKOUT_EVENTS.CLOSE);
    closeTab();
    teardown();
    setTabState(CHECKOUT_TAB_STATES.IDLE);
  }, [closeTab, teardown]);

  /**
   * @returns {boolean} whether the checkout tab is currently open.
   */
  const isCheckoutTabOpen = useCallback(() => isTabOpen(tabRef.current), []);

  // Notice the guest closing the checkout tab. There is no event for another
  // tab closing, so the handle is polled while one is expected to be open.
  useEffect(() => {
    if (tabState !== CHECKOUT_TAB_STATES.OPEN && tabState !== CHECKOUT_TAB_STATES.PREPARING) return undefined;
    const interval = setInterval(() => {
      if (!tabRef.current || isTabOpen(tabRef.current)) return;
      if (tabState === CHECKOUT_TAB_STATES.PREPARING) {
        // Closed before the booking was handed over; launch() reports it.
        return;
      }
      setTabState(CHECKOUT_TAB_STATES.CLOSED);
      handlersRef.current.onTabClosed?.();
    }, CHECKOUT_TAB_POLL_MS);
    return () => clearInterval(interval);
  }, [tabState]);

  // Leaving the booking screen entirely: drop the channel. The stored
  // payload stays, so an open checkout tab can still finish on its own.
  useEffect(() => closeChannel, [closeChannel]);

  return { tabState, prepare, launch, reopen, end, isCheckoutTabOpen };
};
